/* Renders the compact mobile order summary view with local submission feedback as a secondary section. */
import React from 'react';
import {type MobileOrderSummaryRecord} from '../../mobile-order-summary.js';
import {type MobileOrderSubmission} from '../../mobile-order-types.js';

type MobileOrdersPageProps = {
  orders: MobileOrderSubmission[];
  summaryErrorMessage?: string;
  summaryOrders: MobileOrderSummaryRecord[];
};

const statusLabels: Record<MobileOrderSubmission['status'], string> = {
  pending: 'Pending',
  accepted: 'Accepted',
  rejected: 'Rejected',
};

type MobileSummarySideTone = 'buy' | 'sell' | 'algo' | 'neutral';

/** Formats an ISO timestamp into a compact intraday order-list timestamp. */
function formatOrderTime(timestamp: string): string {
  return new Intl.DateTimeFormat('en-US', {
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(timestamp));
}

/** Maps order sides into CSS tone names so compact rows can carry side color without service coupling. */
function getSummarySideTone(sideLabel: string): MobileSummarySideTone {
  const normalizedSide = sideLabel.trim().toLowerCase();
  if (normalizedSide === 'buy') {
    return 'buy';
  }

  if (normalizedSide === 'sell') {
    return 'sell';
  }

  if (normalizedSide === 'algo') {
    return 'algo';
  }

  return 'neutral';
}

/** Builds the compact summary card class list from order status and side display fields. */
function getSummaryCardClassName(order: MobileOrderSummaryRecord): string {
  const sideTone = getSummarySideTone(order.sideLabel);
  return [
    'mobile-order-card',
    'mobile-summary-card',
    'mobile-summary-card-dense',
    `mobile-summary-card-${order.statusTone}`,
    `mobile-summary-side-${sideTone}`,
  ].join(' ');
}

/** Renders one single-row live order summary card optimized for phone-width status scanning. */
function MobileOrderSummaryCard({order}: {order: MobileOrderSummaryRecord}) {
  const sideTone = getSummarySideTone(order.sideLabel);

  return (
    <article
      aria-label={`Order ${order.traderLabel} ${order.symbolLabel}`}
      className={getSummaryCardClassName(order)}
    >
      <div className='mobile-summary-dense-row'>
        <span className={`mobile-side-pill mobile-side-pill-${sideTone}`} aria-label={`Side ${order.sideLabel}`}>
          Side {order.sideLabel}
        </span>
        <span className={`mobile-status-chip mobile-state-chip summary-${order.statusTone}`} aria-label={`State ${order.statusLabel}`}>
          State {order.statusLabel}
        </span>
        <strong>{order.symbolLabel}</strong>
        <span className='mobile-trader-label'>{order.traderLabel}</span>
        <span>Qty {order.orderQuantity}</span>
        <span>@ {order.priceLabel}</span>
        <span>{order.filledQuantity}/{order.orderQuantity}</span>
        <span>{order.remainingQuantity} left</span>
      </div>
    </article>
  );
}

/** Renders one mobile-submission status row for immediate local feedback after submit. */
function MobileSessionOrderCard({order}: {order: MobileOrderSubmission}) {
  return (
    <article className={`mobile-order-card mobile-session-card mobile-session-card-${order.status}`}>
      <div className='mobile-summary-primary'>
        <div>
          <strong>{order.id}</strong>
          <span>{order.symbol}</span>
        </div>
        <span className={`mobile-status-chip ${order.status}`}>{statusLabels[order.status]}</span>
      </div>
      <div className='mobile-summary-details'>
        <span>{order.side === 'buy' ? 'Buy' : 'Sell'} {order.quantity}</span>
        <span>{order.orderType === 'limit' ? 'Limit' : 'Market'}</span>
        <span>{order.limitPrice ?? 'MKT'}</span>
        <span>{formatOrderTime(order.timestamp)}</span>
      </div>
      <div className='mobile-order-account'>{order.accountLabel}</div>
      {order.message && <p className='mobile-order-message'>{order.message}</p>}
    </article>
  );
}

/** Renders order summary cards first, then newest-first local mobile submission status cards. */
const MobileOrdersPage: React.FC<MobileOrdersPageProps> = ({
  orders,
  summaryErrorMessage,
  summaryOrders,
}) => (
  <article className='mobile-orders-page'>
    <header className='mobile-page-header'>
      <div>
        <p className='mobile-eyebrow'>Order Summary</p>
        <h1>Orders</h1>
      </div>
      <span className='mobile-order-count'>{summaryOrders.length}</span>
    </header>

    {summaryErrorMessage && (
      <section className='mobile-orders-alert' role='status'>
        {summaryErrorMessage}
      </section>
    )}

    {summaryOrders.length === 0
      ? (
        <section className='mobile-empty-orders'>
          <h2>No order summary yet</h2>
          <p>Order Summary updates will appear here when the stream sends records.</p>
        </section>
      )
      : (
        <section className='mobile-order-list' aria-label='Order summary'>
          {summaryOrders.map(order => <MobileOrderSummaryCard key={order.id} order={order} />)}
        </section>
      )}

    {orders.length > 0 && (
      <section className='mobile-session-orders' aria-label='Recent mobile submissions'>
        <div className='mobile-section-heading'>
          <h2>Recent Mobile Submissions</h2>
          <span>{orders.length}</span>
        </div>
        <div className='mobile-order-list'>
          {orders.map(order => <MobileSessionOrderCard key={order.clientId} order={order} />)}
        </div>
      </section>
    )}
  </article>
);

export default MobileOrdersPage;
